// Active-branch selection for the org admin.
//
// When an org admin logs in with branch_avail=true, they pick a branch on the
// BranchSelect page; that choice is stored here and sent as ?branch= on
// branch-scoped list APIs (e.g. the employee list). A branch admin / employee
// never uses this — their data is already server-scoped to their own branch.
//
// TWO independent pieces of state:
//   * MODE  (active_branch_mode): 'overview' | 'branch'  — what the org admin
//     entered from the selection page. In 'overview' the sidebar/reports are the
//     restricted org-wide view; this NEVER changes while in overview, even when
//     the branch filter below changes.
//   * FILTER (active_branch_id): 'all' | <branch id> — which branch's data is
//     currently shown. In overview a header dropdown flips this between 'all'
//     and a specific branch WITHOUT leaving overview (so the sidebar is stable).
const ACTIVE_BRANCH_ID = "active_branch_id";
const ACTIVE_BRANCH_NAME = "active_branch_name";
const ACTIVE_BRANCH_MAIN = "active_branch_main";
const ACTIVE_BRANCH_MODE = "active_branch_mode";

// --- entering a specific branch (full app, branch-scoped) ---
export const setActiveBranch = (id, name = "", isMain = false) => {
    if (id === null || id === undefined || id === "") {
        clearActiveBranch();
        return;
    }
    sessionStorage.setItem(ACTIVE_BRANCH_MODE, "branch");
    sessionStorage.setItem(ACTIVE_BRANCH_ID, String(id));
    sessionStorage.setItem(ACTIVE_BRANCH_NAME, name || "");
    sessionStorage.setItem(ACTIVE_BRANCH_MAIN, isMain ? "true" : "false");
};

// --- entering org-wide Dashboard Overview mode ---
// filterId defaults to 'all'; a header dropdown can later narrow it to a
// specific branch id (name optional) while STAYING in overview mode.
export const setOverviewMode = (filterId = "all", filterName = "") => {
    sessionStorage.setItem(ACTIVE_BRANCH_MODE, "overview");
    sessionStorage.setItem(ACTIVE_BRANCH_ID, String(filterId));
    sessionStorage.setItem(ACTIVE_BRANCH_NAME, filterName || "");
    sessionStorage.setItem(ACTIVE_BRANCH_MAIN, "false");
};

export const getActiveBranchId = () => {
    const v = sessionStorage.getItem(ACTIVE_BRANCH_ID);
    return v && v !== "null" && v !== "" ? v : null;
};

export const getActiveBranchName = () =>
    sessionStorage.getItem(ACTIVE_BRANCH_NAME) || "";

// Whether the currently active branch is the organisation's main branch.
export const getActiveBranchIsMain = () =>
    sessionStorage.getItem(ACTIVE_BRANCH_MAIN) === "true";

export const clearActiveBranch = () => {
    sessionStorage.removeItem(ACTIVE_BRANCH_ID);
    sessionStorage.removeItem(ACTIVE_BRANCH_NAME);
    sessionStorage.removeItem(ACTIVE_BRANCH_MAIN);
    sessionStorage.removeItem(ACTIVE_BRANCH_MODE);
};

// True when the logged-in user is an org admin who has branches to choose from.
export const isBranchAvail = () =>
    sessionStorage.getItem("branch_avail") === "true";

// Boolean key: is this an ORG ADMIN currently in org-wide "Dashboard Overview"
// mode? Driven by the MODE flag, NOT by which branch is filtered — so narrowing
// the overview to one branch via the header dropdown keeps this true and the
// restricted sidebar/reports intact.
export const isOrgOverview = () =>
    isBranchAvail() && sessionStorage.getItem(ACTIVE_BRANCH_MODE) === "overview";

// The branch the overview is currently filtered to: null means "all branches"
// (no filter). A concrete id scopes overview data to that branch.
export const getOverviewBranchFilter = () => {
    const v = getActiveBranchId();
    return v && v !== "all" ? v : null;
};

// Event fired when the overview branch filter changes, so branch-scoped pages
// (employee list, reports) can re-fetch WITHOUT a full page reload. Listeners
// re-read getActiveBranchId()/getOverviewBranchFilter() and refresh their data.
export const BRANCH_FILTER_CHANGED_EVENT = "hrms:branch-filter-changed";

// Change the overview branch filter (stay in overview mode) and notify listeners.
export const switchOverviewBranch = (filterId = "all", filterName = "") => {
    setOverviewMode(filterId, filterName);
    window.dispatchEvent(new Event(BRANCH_FILTER_CHANGED_EVENT));
};
